import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  InputAdornment,
  Pagination,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import IconButton from '@mui/material/IconButton';
import { useAdminHosts } from '../../hooks/useAdmin';

function formatCurrency(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatRelativeDate(dateStr: string | null): string {
  if (!dateStr) return 'Never';

  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes} min ago`;
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

const LIMIT = 20;

export default function AdminHostsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  // Debounce search: apply after user stops typing for 400ms
  const debounceTimer = useMemo(() => ({ id: null as ReturnType<typeof setTimeout> | null }), []);

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    if (debounceTimer.id) clearTimeout(debounceTimer.id);
    debounceTimer.id = setTimeout(() => {
      setSearch(value);
      setPage(1);
    }, 400);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const { data, isLoading, error } = useAdminHosts(page, LIMIT, search);

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load hosts. Please try again later.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
        Host Management
      </Typography>

      {/* Search bar - sticky on mobile */}
      <Box
        sx={{
          position: 'sticky',
          top: { xs: 56, sm: 64 },
          zIndex: 10,
          bgcolor: 'background.default',
          py: 1.5,
          mb: 2,
        }}
      >
        <TextField
          fullWidth
          placeholder="Search hosts by name or email..."
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
          size="small"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton onClick={clearSearch} edge="end" size="small" aria-label="Clear search">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
              sx: { minHeight: 44 },
            },
          }}
        />
      </Box>

      {/* Host cards */}
      <Stack spacing={1.5}>
        {isLoading
          ? Array.from({ length: 5 }).map((_, i) => (
              <Card key={i}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Skeleton variant="text" width="50%" height={24} />
                  <Skeleton variant="text" width="70%" height={18} sx={{ mt: 0.5 }} />
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Skeleton variant="rounded" width={60} height={24} />
                    <Skeleton variant="rounded" width={70} height={24} />
                  </Box>
                  <Skeleton variant="text" width="80%" height={18} sx={{ mt: 1 }} />
                </CardContent>
              </Card>
            ))
          : data?.data && data.data.length > 0
            ? data.data.map((host) => (
                <Card key={host.id}>
                  <CardActionArea
                    onClick={() => navigate(`/admin/hosts/${host.id}`)}
                    sx={{ minHeight: 44 }}
                  >
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                      {/* Name + email */}
                      <Typography variant="body1" fontWeight={600} sx={{ fontSize: { xs: 15, sm: 16 } }}>
                        {host.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: 13, sm: 14 }, mt: 0.25 }}>
                        {host.email}
                      </Typography>

                      {/* Plan + Status chips */}
                      <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                        <Chip
                          label={host.plan}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: 12, height: 26 }}
                        />
                        <Chip
                          label={host.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          color={host.isActive ? 'success' : 'default'}
                          sx={{ fontSize: 12, height: 26 }}
                        />
                        {host.isPlatformAdmin && (
                          <Chip
                            label="Admin"
                            size="small"
                            color="primary"
                            sx={{ fontSize: 12, height: 26 }}
                          />
                        )}
                      </Box>

                      {/* Stats row */}
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1.5, fontSize: { xs: 13, sm: 14 } }}
                      >
                        {host.eventsCount} events &middot; {host.totalGuestsCount} guests &middot;{' '}
                        {formatCurrency(host.totalRevenueCents)} revenue
                      </Typography>

                      {/* Last login */}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.5, fontSize: { xs: 12, sm: 13 } }}
                      >
                        Last login: {formatRelativeDate(host.lastLoginAt)}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              ))
            : !isLoading && (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Typography variant="body1" color="text.secondary">
                    {search ? 'No hosts match your search.' : 'No hosts found.'}
                  </Typography>
                </Box>
              )}
      </Stack>

      {/* Pagination */}
      {data && data.totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, pb: 2 }}>
          <Pagination
            count={data.totalPages}
            page={page}
            onChange={(_, newPage) => setPage(newPage)}
            color="primary"
            size="large"
            sx={{
              '& .MuiPaginationItem-root': {
                minWidth: 44,
                minHeight: 44,
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
}
